import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, ArrowLeft, ShieldCheck, Lock, Database, Trash2, Globe } from "lucide-react";

export default function Privacy() {
  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)] antialiased">
      <header className="sticky top-0 z-40 bg-[rgba(9,9,11,0.85)] backdrop-blur-md border-b border-[var(--border)]">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 py-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-500 grid place-items-center">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={1.8} />
            </div>
            <span className="font-display font-semibold text-xl tracking-tight">Prosperr</span>
          </Link>
          <Link to="/" className="text-sm font-medium text-zinc-400 hover:text-white flex items-center gap-1.5 transition-colors">
            <ArrowLeft className="w-4 h-4" /> Accueil
          </Link>
        </div>
      </header>

      <section className="max-w-3xl mx-auto px-6 py-16 sm:py-24">
        <div className="hp-label">Politique de confidentialité</div>
        <h1 className="mt-3 font-display font-semibold tracking-tight leading-[0.98] text-4xl sm:text-5xl">
          Vos données <span className="text-zinc-500">restent votre propriété.</span>
        </h1>
        <p className="mt-6 text-base text-zinc-400 leading-relaxed">
          Prosperr est conforme au Règlement Général sur la Protection des Données (RGPD). Nous collectons uniquement les informations nécessaires au fonctionnement de votre assistant commercial. Vous gardez le contrôle complet de vos données à tout moment.
        </p>

        <div className="mt-12 space-y-0 border-y border-[var(--border)] divide-y divide-[var(--border-soft)]">
          {[
            { Icon: ShieldCheck, title: "Hébergement européen", body: "Vos données sont stockées exclusivement dans l'UE (Paris / Francfort). Aucun transfert hors Europe." },
            { Icon: Lock, title: "Chiffrement TLS et mots de passe hachés", body: "Tous les échanges utilisent TLS 1.3. Les mots de passe sont hachés avec bcrypt (cost factor 12). Aucune donnée en clair dans nos logs." },
            { Icon: Database, title: "Usage strictement nécessaire", body: "Identifiant, email, nom, coordonnées professionnelles, prospects que vous créez. Aucune donnée de navigation, aucun pixel publicitaire, aucun tracker tiers." },
            { Icon: Trash2, title: "Droit à l'oubli en un clic", body: "Depuis votre profil → Vie privée et RGPD, vous pouvez exporter toutes vos données ou supprimer définitivement votre compte. Suppression effective sous 24h." },
            { Icon: Globe, title: "Portabilité et export", body: "Téléchargez à tout moment l'intégralité de vos prospects en Excel, CSV ou JSON. Aucun lock-in." },
          ].map(({ Icon, title, body }, i) => (
            <div key={i} className="py-8 flex items-start gap-5">
              <div className="w-11 h-11 rounded-lg border border-[var(--border)] bg-[var(--surface-alt)] grid place-items-center shrink-0 mt-1">
                <Icon className="w-4 h-4 text-emerald-400" strokeWidth={1.8} />
              </div>
              <div>
                <h2 className="font-display font-semibold text-lg sm:text-xl text-[var(--text)] leading-tight">{title}</h2>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">{body}</p>
              </div>
            </div>
          ))}
        </div>

        <h2 className="mt-16 font-display font-semibold text-2xl text-[var(--text)] tracking-tight">Ce que nous collectons, et pourquoi</h2>
        <div className="mt-6 hp-card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--border-soft)] bg-[var(--surface-alt)]">
                <th className="text-left p-4 font-display font-semibold text-zinc-300">Donnée</th>
                <th className="text-left p-4 font-display font-semibold text-zinc-300">Raison</th>
                <th className="text-left p-4 font-display font-semibold text-zinc-300">Base légale</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-soft)]">
              {[
                ["Email et mot de passe", "Authentification, récupération de compte", "Exécution du contrat"],
                ["Nom, téléphone, photo", "Votre carte de visite digitale", "Consentement"],
                ["Prospects et interactions", "Votre tunnel commercial", "Exécution du contrat"],
                ["Géolocalisation", "Radar terrain, tournée optimisée", "Consentement explicite à l'activation"],
                ["Session cookie", "Vous maintenir connecté pendant 7 jours", "Intérêt légitime"],
              ].map(([a, b, c], i) => (
                <tr key={i}>
                  <td className="p-4 font-medium text-[var(--text)]">{a}</td>
                  <td className="p-4 text-zinc-400">{b}</td>
                  <td className="p-4 text-zinc-500 text-xs">{c}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <h2 className="mt-16 font-display font-semibold text-2xl text-[var(--text)] tracking-tight">Durée de conservation</h2>
        <ul className="mt-6 space-y-3 text-sm text-zinc-400">
          <li><strong className="text-[var(--text)]">Compte actif</strong> — données conservées tant que votre abonnement est actif.</li>
          <li><strong className="text-[var(--text)]">Compte inactif</strong> — suppression automatique après 24 mois sans connexion, après notification.</li>
          <li><strong className="text-[var(--text)]">Suppression demandée</strong> — effective sous 24h, anonymisation immédiate.</li>
          <li><strong className="text-[var(--text)]">Obligations légales</strong> — facturation conservée 10 ans (Code de commerce).</li>
        </ul>

        <h2 className="mt-16 font-display font-semibold text-2xl text-[var(--text)] tracking-tight">Contact et réclamations</h2>
        <p className="mt-4 text-sm text-zinc-400 leading-relaxed">
          Pour toute question, export, rectification ou suppression : écrivez à <a href="mailto:privacy@prosperr.xyz" className="text-emerald-400 font-medium underline underline-offset-4 hover:text-emerald-300">privacy@prosperr.xyz</a>.
        </p>
        <p className="mt-3 text-sm text-zinc-500 leading-relaxed">
          En cas de litige non résolu, vous pouvez saisir la <a href="https://www.cnil.fr/fr/plaintes" target="_blank" rel="noreferrer" className="text-emerald-400 font-medium underline underline-offset-4 hover:text-emerald-300">CNIL</a>.
        </p>

        <div className="mt-16 pt-8 border-t border-[var(--border-soft)] text-xs text-zinc-600">
          Dernière mise à jour : {new Date().toLocaleDateString("fr-FR", { month: "long", year: "numeric" })} · Prosperr, France
        </div>
      </section>
    </div>
  );
}
